import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import axios from 'axios';
import { useDebounce } from 'use-debounce';

export interface RNWorkOrderRow {
  _id: string;
  number?: string;
  items?: { _id: string; ident?: string; name?: string } | string;
  ingredient?: any;
  updatedAt?: string;
}

// Search-list source for the РН toolbar modal — lists existing /api/rn-work-orders
// documents (NOT the OS /api/ingredients normativ). Mirrors the list-accumulation +
// infinite-scroll pattern from useRNDetail's fetchIngredientsList.
export const useRNWorkOrderList = (documentCode?: string) => {
  const [rnList, setRnList] = useState<RNWorkOrderRow[]>([]);
  // The screen uses this as the first stage of its single hydration loader.
  // Start true so the initial render cannot flash an empty work-order shell
  // before the first list request begins in useEffect.
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 300);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [absoluteTotal, setAbsoluteTotal] = useState<number>(0);
  const [reloadFlag, setReloadFlag] = useState(0);
  const requestId = useRef(0);
  const lastFetchKeyRef = useRef<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL ?? '';
  const baseUrl = `${apiUrl}/api/rn-work-orders`;

  const fetchRnList = useCallback(async (pageToFetch: number, resetList = false) => {
    const reqId = ++requestId.current;
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.get<{
        rnWorkOrders: RNWorkOrderRow[];
        page: number;
        totalPages: number;
        total: number;
        absoluteTotal: number;
      }>(baseUrl, {
        headers: { 'x-auth-token': token },
        params: {
          search: debouncedSearch,
          page: pageToFetch,
          limit: 25,
          documentCode,
        },
      });

      if (reqId !== requestId.current) return;

      if (resetList || pageToFetch === 1) {
        setRnList(response.data.rnWorkOrders);
      } else {
        setRnList(prev => [...prev, ...response.data.rnWorkOrders]);
      }
      setTotal(response.data.total);
      setAbsoluteTotal(response.data.absoluteTotal);
      setPage(response.data.page);
      setTotalPages(response.data.totalPages);
    } catch (err: any) {
      if (reqId !== requestId.current) return;
      const message = err.response?.data?.message || err.message || 'Failed to fetch work orders';
      setError(message);
      if (resetList || pageToFetch === 1) setRnList([]);
    } finally {
      if (reqId === requestId.current) setLoading(false);
    }
  }, [baseUrl, debouncedSearch, documentCode]);

  // Reset to page 1 when search changes (must run before the fetch effect)
  useLayoutEffect(() => {
    setPage(1);
    setRnList([]);
  }, [debouncedSearch]);

  // Fetch list when search, page, or reload flag changes
  useEffect(() => {
    const fetchKey = `${documentCode}\u0000${debouncedSearch}\u0000${page}\u0000${reloadFlag}`;
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    if (page === 1) fetchRnList(1, true);
    else fetchRnList(page, false);
  }, [fetchRnList, page, reloadFlag, debouncedSearch]);

  // Reload function for modal open — resets state and forces fresh fetch
  const reloadRnList = useCallback(() => {
    setPage(1);
    setRnList([]);
    setReloadFlag(prev => prev + 1);
  }, []);

  // Fetch the single most-recent РН row (page 1, limit 1) and return it, or null.
  // Used by the delete flow to load the latest remaining РН (mirrors 1000's fetchLatestDocumentByCode).
  const fetchLatestRnRow = useCallback(async (): Promise<RNWorkOrderRow | null> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const response = await axios.get<{ rnWorkOrders: RNWorkOrderRow[] }>(baseUrl, {
        headers: { 'x-auth-token': token },
        params: { page: 1, limit: 1, documentCode },
      });
      const rows = response.data.rnWorkOrders || [];
      return rows.length > 0 ? rows[0] : null;
    } catch {
      return null;
    }
  }, [baseUrl]);

  // Register navigation targets any RN, including one outside the first search page.
  const fetchRnRowById = useCallback(async (id: string): Promise<RNWorkOrderRow> => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authentication token missing');
    const response = await axios.get<RNWorkOrderRow>(`${baseUrl}/${id}`, {
      headers: { 'x-auth-token': token },
    });
    return response.data;
  }, [baseUrl]);

  return {
    rnList,
    loading,
    error,
    page,
    totalPages,
    total,
    absoluteTotal,
    setPage,
    search,
    setSearch,
    reloadRnList,
    fetchLatestRnRow,
    fetchRnRowById,
  };
};
